import { defineStore, storeToRefs } from 'pinia';
import type { Ref, WatchHandle } from 'vue';
import { computed, ref, watch } from 'vue';

import {
  DomainBranding,
  DomainReference,
  Loadable,
  Optional,
} from '@rygen/rygen-ui/model';

import { Order, QuickQuoteRequest, Shipment } from '@/model';
import { ConsolidatedShipment } from '@/model/consolidation';
import { RouteBuilderRequest } from '@/model/routeBuilder';
import { RouteBuilderPhase2Request } from '@/model/routeBuilder/phase2';

import { useCurrentUserStore } from './CurrentUser';
import { useDomainStore } from './Domain';

export type ParentEntity =
  | Order
  | Shipment
  | ConsolidatedShipment
  | RouteBuilderRequest
  | RouteBuilderPhase2Request
  | QuickQuoteRequest;

export interface ParentEntityStore {
  clearParentEntity: () => void;
  clientBranding: Ref<Loadable<DomainBranding>>;
  includeSubDomains: Ref<boolean>;
  isOwningClientSubOfCurrentDomain: Ref<boolean>;
  loadClientBranding: (clientId: number) => Promise<void>;
  owningClient: Ref<Optional<DomainReference>>;
  parentEntity: Ref<Optional<ParentEntity>>;
  setParentEntity: (newVal: Ref<Optional<ParentEntity>>) => void;
}

const _useParentEntityStore = defineStore(
  'parentEntity',
  (): ParentEntityStore => {
    const { selectedDomainId } = useCurrentUserStore();
    const { loadDomainBranding } = useDomainStore();

    const _parentEntity: Ref<Optional<ParentEntity>> = ref();
    const _unwatchParentEntity: Ref<Optional<WatchHandle>> = ref();

    const clientBranding: Ref<Loadable<DomainBranding>> = ref({
      loading: false,
    });

    const parentEntity = computed(() => _parentEntity?.value);

    const owningClient = computed(() => parentEntity.value?.owner);

    /**
     * Whether we want to include entities owned by subdomains in API responses
     *
     * We only want to include subdomains on list pages. This allows users to
     * search for any data within their domain tree. On entry/detail pages, we
     * only want entities owned by the selected client.
     */
    const includeSubDomains = computed(() => {
      return parentEntity.value === undefined;
    });

    const isOwningClientSubOfCurrentDomain = computed(() => {
      return (
        !!owningClient.value?.id &&
        owningClient.value.id !== selectedDomainId.value
      );
    });

    const clearParentEntity = () => {
      _unwatchParentEntity.value?.();
      _unwatchParentEntity.value = undefined;
      _parentEntity.value = undefined;
    };

    const setParentEntity = (newVal: Ref<Optional<ParentEntity>>) => {
      _parentEntity.value = newVal.value;

      _unwatchParentEntity.value?.();
      _unwatchParentEntity.value = watch(
        newVal,
        (newValInWatcher) => {
          _parentEntity.value = newValInWatcher;
        },
        { deep: true },
      );
    };

    const loadClientBranding = async (clientId: number) => {
      clientBranding.value = await loadDomainBranding(clientId);
    };

    watch(selectedDomainId, (newVal, oldVal) => {
      if (newVal !== oldVal) {
        clearParentEntity();
      }
    });

    return {
      clearParentEntity,
      clientBranding,
      includeSubDomains,
      isOwningClientSubOfCurrentDomain,
      loadClientBranding,
      owningClient,
      parentEntity,
      setParentEntity,
    };
  },
);

export const useParentEntityStore = (): ParentEntityStore => {
  const store = _useParentEntityStore();
  const {
    clientBranding,
    includeSubDomains,
    isOwningClientSubOfCurrentDomain,
    owningClient,
    parentEntity,
  } = storeToRefs(store);
  const { clearParentEntity, loadClientBranding, setParentEntity } = store;

  return {
    clearParentEntity,
    clientBranding,
    includeSubDomains,
    isOwningClientSubOfCurrentDomain,
    loadClientBranding,
    owningClient,
    parentEntity,
    setParentEntity,
  };
};
